import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getOrder,
  clearOrder,
  updateOrderStatus,
} from "../../redux/slices/orderSlice";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import type { OrderStatus } from "../../types/order/order.model";

type Props = {
  selectedId: string;
  onBack: () => void;
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: "Chờ xác nhận",
  confirmed: "Đã xác nhận",
  shipping: "Đang giao",
  completed: "Hoàn thành",
  cancelled: "Đã hủy",
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  shipping: "bg-indigo-100 text-indigo-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const VALID_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending: ["confirmed", "cancelled"],
  confirmed: ["shipping", "cancelled"],
  shipping: ["completed", "cancelled"],
  completed: [],
  cancelled: [],
};

export const OrderDetail = ({ selectedId, onBack }: Props) => {
  const dispatch = useAppDispatch();
  const { order } = useAppSelector((state) => state.order);

  const [cancelReason, setCancelReason] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    dispatch(getOrder(selectedId));
    return () => {
      dispatch(clearOrder());
    };
  }, [dispatch, selectedId]);

  const handleUpdateStatus = async (newStatus: OrderStatus) => {
    if (newStatus === "cancelled") {
      setShowCancelModal(true);
      return;
    }
    try {
      await dispatch(
        updateOrderStatus({ id: selectedId, status: newStatus }),
      ).unwrap();
      toast.success(`Đã chuyển trạng thái sang "${STATUS_LABELS[newStatus]}"`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error("Vui lòng nhập lý do hủy đơn");
      return;
    }
    try {
      await dispatch(
        updateOrderStatus({
          id: selectedId,
          status: "cancelled",
          cancelReason,
        }),
      ).unwrap();
      toast.success("Đã hủy đơn hàng");
      setShowCancelModal(false);
      setCancelReason("");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const formatCurrency = (amount: number) =>
    amount.toLocaleString("vi-VN", { style: "currency", currency: "VND" });

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (!order) {
    return (
      <div className="p-5">
        <p className="text-sm text-gray-500 text-center">
          Đang tải đơn hàng...
        </p>
      </div>
    );
  }

  const nextStatuses = VALID_TRANSITIONS[order.status] || [];

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Chi tiết đơn hàng{" "}
          <span className="text-blue-600">{order.orderCode}</span>
        </h1>
        <button
          type="button"
          onClick={onBack}
          className="p-2 text-sm bg-gray-500 rounded-lg text-white cursor-pointer hover:bg-gray-600 transition-colors duration-300"
        >
          Quay lại
        </button>
      </div>

      {/* Status + Actions */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">Trạng thái:</p>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[order.status]}`}
          >
            {STATUS_LABELS[order.status]}
          </span>
        </div>
        {nextStatuses.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {nextStatuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handleUpdateStatus(s)}
                className={`px-3 py-1.5 text-sm rounded-lg text-white cursor-pointer transition-colors duration-300 ${
                  s === "cancelled"
                    ? "bg-red-500 hover:bg-red-600"
                    : s === "completed"
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cancel reason */}
      {order.cancelReason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700 font-medium">Lý do hủy:</p>
          <p className="text-sm text-red-600 mt-1">{order.cancelReason}</p>
        </div>
      )}

      {/* Customer info */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <p className="text-sm font-semibold text-gray-700">
          Thông tin khách hàng
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-400">Tên</p>
            <p className="text-sm text-gray-700">{order.customer.name}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">SĐT</p>
            <p className="text-sm text-gray-700">{order.customer.phone}</p>
          </div>
          {order.customer.email && (
            <div>
              <p className="text-xs text-gray-400">Email</p>
              <p className="text-sm text-gray-700">{order.customer.email}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-400">Địa chỉ</p>
            <p className="text-sm text-gray-700">{order.customer.address}</p>
          </div>
          {order.customer.note && (
            <div className="md:col-span-2">
              <p className="text-xs text-gray-400">Ghi chú</p>
              <p className="text-sm text-gray-700">{order.customer.note}</p>
            </div>
          )}
        </div>
      </div>

      {/* Order items */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <p className="text-sm font-semibold text-gray-700">Sản phẩm</p>
        <div className="w-full overflow-x-auto scrollbar-custom">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-sm text-gray-500 text-left p-2">
                  Sản phẩm
                </th>
                <th className="text-sm text-gray-500 text-center p-2">
                  Phân loại
                </th>
                <th className="text-sm text-gray-500 text-center p-2">
                  Đơn giá
                </th>
                <th className="text-sm text-gray-500 text-center p-2">SL</th>
                <th className="text-sm text-gray-500 text-right p-2">
                  Thành tiền
                </th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, idx) => (
                <tr key={idx} className="border-b border-gray-100">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-10 w-10 object-cover rounded border border-gray-200"
                        />
                      )}
                      <p className="text-sm text-gray-700">{item.name}</p>
                    </div>
                  </td>
                  <td className="text-sm text-gray-500 text-center p-2">
                    {item.variantName}
                  </td>
                  <td className="text-sm text-gray-700 text-center p-2">
                    {formatCurrency(item.price)}
                  </td>
                  <td className="text-sm text-gray-700 text-center p-2">
                    {item.quantity}
                  </td>
                  <td className="text-sm text-gray-700 text-right p-2 font-medium">
                    {formatCurrency(item.price * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200">
                <td colSpan={4} className="text-sm font-semibold p-2 text-right">
                  Tổng cộng:
                </td>
                <td className="text-sm font-bold text-right p-2 text-blue-600">
                  {formatCurrency(order.totalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Meta */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-400">Thanh toán</p>
            <p className="text-sm text-gray-700">{order.paymentMethod}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Ngày đặt</p>
            <p className="text-sm text-gray-700">
              {formatDate(order.createdAt)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Cập nhật</p>
            <p className="text-sm text-gray-700">
              {formatDate(order.updatedAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Cancel modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 z-50">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm space-y-3">
            <p className="text-lg font-semibold text-gray-700">
              Hủy đơn hàng
            </p>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Lý do hủy <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:text-gray-400 resize-none"
                placeholder="Nhập lý do hủy đơn"
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason("");
                }}
                className="p-2 text-sm text-white bg-gray-500 hover:bg-gray-600 rounded-lg cursor-pointer"
              >
                Đóng
              </button>
              <button
                type="button"
                onClick={handleCancelOrder}
                className="p-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer"
              >
                Xác nhận hủy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
